import { useEffect, useState } from 'react';
import { Avatar, Button, Modal, Spin, Tag, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFormGroups } from '../../services/addFormApi';
import DescriptionBlock from '../detail/DescriptionBlock';

const { Text } = Typography;

function getInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

function getRecordName(record) {
  if (!record) return '';
  if (record.name) return record.name;
  if (record.fullName) return record.fullName;
  if (record.firstName || record.lastName) return [record.firstName, record.lastName].filter(Boolean).join(' ');
  return record.candidateName ?? record.clientName ?? record.jobTitle ?? '';
}

function getRecordEmail(record) {
  if (!record) return '';
  return record.email ?? record.emailId ?? record.emailAddress ?? '';
}

function getRecordStatus(record) {
  if (!record) return '';
  if (record.status) return record.status;
  if (record.stage) return record.stage;
  if (typeof record.isActive === 'boolean') return record.isActive ? 'Active' : 'Inactive';
  return '';
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return value.label ?? value.name ?? Object.values(value).join(' ');
  return String(value);
}

const DESCRIPTION_KEYS = ['jobDescription', 'description', 'rawDescription', 'jobDescriptionText'];

function isJobModule(moduleName) {
  return String(moduleName ?? '').toLowerCase() === 'job';
}

function isDescriptionField(field) {
  const matchesKey = DESCRIPTION_KEYS.includes(field.field);
  const matchesLabel = /description/i.test(field.label ?? '');
  return matchesKey || matchesLabel;
}

function ProfileCard({ record }) {
  const name = getRecordName(record);
  const email = getRecordEmail(record);
  const status = getRecordStatus(record);

  if (!name && !email) return null;

  return (
    <div className="preview-profile-card">
      <div className="preview-profile-card__info">
        <Avatar className="preview-profile-card__avatar">{getInitials(name)}</Avatar>
        <div className="preview-profile-card__text">
          {name && <span className="preview-profile-card__name">{name}</span>}
          {email && <span className="preview-profile-card__email">{email}</span>}
        </div>
      </div>
      {status && <Tag className="preview-profile-card__status">{status}</Tag>}
    </div>
  );
}

function FieldsDisplay({ fields, moduleName }) {
  if (!fields?.length) return null;

  return (
    <div className="preview-field-grid">
      {fields.map((field) => {
        const raw = field.value;
        const isArray = Array.isArray(raw) && raw.length > 0;
        const showFormattedDescription = isJobModule(moduleName)
          && isDescriptionField(field)
          && typeof raw === 'string'
          && raw.trim();

        if (isArray) {
          return raw.map((item, idx) => (
            <div key={`${field.field ?? field.label}-${idx}`} className="preview-field-card">
              <span className="preview-field-card__label">
                {field.label}{raw.length > 1 ? ` (${idx + 1})` : ''}
              </span>
              <span className="preview-field-card__value">{formatValue(item)}</span>
            </div>
          ));
        }

        if (showFormattedDescription) {
          return (
            <div key={field.field ?? field.label} className="preview-field-card preview-field-card--description">
              <span className="preview-field-card__label">{field.label}</span>
              <div className="preview-field-card__description">
                <DescriptionBlock raw={raw} />
              </div>
            </div>
          );
        }

        return (
          <div key={field.field ?? field.label} className="preview-field-card">
            <span className="preview-field-card__label">{field.label}</span>
            <span className="preview-field-card__value">{formatValue(raw)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function PreviewPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { moduleName = '', record = null } = location.state ?? {};

  const id = record?.id ?? record?._id;
  const [formGroups, setFormGroups] = useState([]);
  const [loading, setLoading] = useState(Boolean(moduleName && id));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!moduleName || !id) return;

    let cancelled = false;

    getFormGroups({ module: moduleName, id })
      .then((data) => {
        if (!cancelled) {
          setFormGroups(Array.isArray(data) ? data : []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load preview');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [moduleName, id]);

  function handleClose() { navigate(-1); }

  const modalTitle = (
    <div className="preview-modal-header">
      <span className="preview-modal-module">{moduleName.toUpperCase()}</span>
      <span className="preview-modal-title">{moduleName ? ' Details' : 'Preview'}</span>
    </div>
  );

  const footer = (
    <div className="wizard-footer preview-scroll-footer">
      <div className="wizard-footer-nav">
        <Button onClick={handleClose}>Close</Button>
      </div>
    </div>
  );

  function renderBody() {
    if (!moduleName || !id) {
      return <div className="job-preview-content job-preview-status">Preview id is missing.</div>;
    }
    if (loading) {
      return <div className="job-preview-content job-preview-status"><Spin size="small" /></div>;
    }
    if (error) {
      return <div className="job-preview-content job-preview-status job-preview-error">{error}</div>;
    }
    if (!formGroups.length) {
      return <div className="job-preview-content job-preview-status">No form groups found.</div>;
    }

    return (
      <div className="preview-section-list">
        {formGroups.map((group, index) => (
          <section key={group.name ?? group.id ?? index} className="preview-section-card">
            <div className="preview-section-card__header">
              <span className="preview-section-card__number">{index + 1}</span>
              <span className="preview-section-card__title">
                {group.label ?? group.name ?? `Section ${index + 1}`}
              </span>
            </div>
            <div className="preview-section-card__body">
              {group.fields?.length > 0 ? (
                <FieldsDisplay fields={group.fields} moduleName={moduleName} />
              ) : (
                <Text type="secondary">No fields in this group.</Text>
              )}
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <Modal
      centered
      className="wizard-modal preview-modal preview-scroll-modal"
      footer={footer}
      open
      title={modalTitle}
      width="min(900px, calc(100vw - 32px))"
      onCancel={handleClose}
      maskClosable={false}
    >
      <div className="wizard-body preview-scroll-body">
        {record && <ProfileCard record={record} />}
        {renderBody()}
      </div>
    </Modal>
  );
}
